import type { ReactElement } from 'react';

import { useRegistry } from '../api/planning-hooks';
import type { RegistryFilters } from '../lib/filters';
import { summarizeProjects } from '../lib/capacity';
import { useFilters } from '../state/FiltersProvider';

interface Chip {
  key: string;
  label: string;
  count: number;
  active: boolean;
  onClick: () => void;
  tone?: 'bad';
}

/**
 * F-13: the header summary — Всего / В работе / Согласовано / Срочно / At-risk /
 * Просрочено — computed from the WHOLE registry (never from the filtered set,
 * so the chips keep their meaning as filters narrow the view) and rendered on
 * every view. Clicking a chip sets that filter; clicking it again clears it.
 */
export function SummaryChips(): ReactElement | null {
  const { data } = useRegistry();
  const { filters, setFilters, clear } = useFilters();
  const allActive = filtersActive(filters);
  if (!data) return null;

  const summary = summarizeProjects(data.projects);

  const toggle = (patch: RegistryFilters) => {
    // A chip is "active" when the current filter equals its patch exactly —
    // the multi-value keys compare as SETS, not by reference.
    const sameFilter = (a: unknown, b: unknown): boolean => {
      if (Array.isArray(a) && Array.isArray(b)) return a.length === b.length && a.every((item) => b.includes(item));
      return a === b;
    };
    const isActive = Object.entries(patch).every(([key, next]) =>
      sameFilter((filters as Record<string, unknown>)[key], next),
    );
    if (isActive) {
      const cleared: Record<string, undefined> = {};
      for (const key of Object.keys(patch)) cleared[key] = undefined;
      setFilters(cleared as RegistryFilters);
    } else {
      setFilters(patch);
    }
  };

  const chips: Chip[] = [
    { key: 'total', label: 'Всего', count: summary.total, active: !allActive, onClick: clear },
    {
      key: 'in_work',
      label: 'В работе',
      // Chips stay keyed to the SEMANTIC built-in ids (row tints and overdue
      // semantics too); `?? 0` only guards the (impossible per normalization,
      // but typed) case of a missing built-in bucket.
      count: summary.byStatus.in_work ?? 0,
      active: filters.status?.includes('in_work') === true,
      onClick: () => toggle({ status: ['in_work'] }),
    },
    {
      key: 'agreed',
      label: 'Согласовано',
      count: summary.byStatus.agreed ?? 0,
      active: filters.status?.includes('agreed') === true,
      onClick: () => toggle({ status: ['agreed'] }),
    },
    {
      key: 'urgent',
      label: 'Срочно',
      count: data.projects.filter((p) => p.priority === 'urgent').length,
      active: filters.priority?.includes('urgent') === true,
      onClick: () => toggle({ priority: ['urgent'] }),
    },
    {
      key: 'risk',
      label: 'At-risk',
      count: summary.atRisk,
      active: filters.atRisk === true,
      onClick: () => toggle({ atRisk: true }),
    },
    {
      key: 'overdue',
      label: 'Просрочено',
      count: summary.overdue,
      active: filters.overdue === true,
      onClick: () => toggle({ overdue: true }),
      tone: 'bad',
    },
  ];

  return (
    <div className="summary-chips" role="group" aria-label="Сводка по реестру" data-testid="summary-chips">
      {chips.map((chip) => (
        <button
          key={chip.key}
          type="button"
          className={[
            'summary-chip',
            chip.active ? 'summary-chip--active' : '',
            chip.tone === 'bad' && chip.count > 0 ? 'summary-chip--bad' : '',
          ]
            .filter(Boolean)
            .join(' ')}
          aria-pressed={chip.active}
          title={
            chip.active
              ? `Сбросить фильтр «${chip.label}»`
              : `Показать только «${chip.label}»`
          }
          onClick={chip.onClick}
        >
          <span className="summary-chip__num">{chip.count}</span>
          <span className="summary-chip__label">{chip.label}</span>
        </button>
      ))}
    </div>
  );
}

/** True when at least one filter narrows the view. */
function filtersActive(filters: ReturnType<typeof useFilters>['filters']): boolean {
  return Object.keys(filters).some((key) => {
    const value = filters[key as keyof typeof filters];
    // An EMPTY multi-value list is no filter (same rule as hasActiveFilters).
    return Array.isArray(value) ? value.length > 0 : value !== undefined;
  });
}
